import React, { useEffect, useState } from "react";
import { Button, Modal, Select, Form, Input, message } from "antd";
import { StopOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { hasPermission } from "../store/userSlice";
import { cancelInfraction } from "../api/infractions/infractionRequest";
import { getMotifsInfraction } from "../api/motifs-annulation/motifRequests";

const { TextArea } = Input;

export default function CancelInfraction({ infractionId, onCancelled }) {
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const [motifs, setMotifs] = useState([]);
  const [loadingMotifs, setLoadingMotifs] = useState(false);
  const canCancel = useSelector((state) => hasPermission(state, "infractions", "cancel"));

  useEffect(() => {
    let mounted = true;
    const fetchMotifs = async () => {
      if (!canCancel) return;
      setLoadingMotifs(true);
      try {
        const data = await getMotifsInfraction();
        if (mounted) setMotifs(data);
      } catch (error) {
        console.error("Failed to fetch motifs:", error);
        message.error("Impossible de charger les motifs d'annulation");
      } finally {
        if (mounted) setLoadingMotifs(false);
      }
    };

    // Fetch motifs only when modal is opened to avoid unnecessary requests
    if (visible) fetchMotifs();

    return () => {
      mounted = false;
    };
  }, [visible, canCancel]);

  if (!canCancel) return null;

  const showModal = () => setVisible(true);

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
  };

  const handleOk = async (values) => {
    try {
      await cancelInfraction(infractionId, {
        cancellation_reason_id: values.cancellation_reason_id,
        cancellation_reason_description: values.cancellation_reason_description || "",
      });
      message.success("Infraction annulée avec succès");
      setVisible(false);
      form.resetFields();
      if (typeof onCancelled === "function") onCancelled();
    } catch (error) {
      console.error("Failed to cancel infraction:", error);
      message.error("Échec de l'annulation de l'infraction");
    }
  };

  return (
    <>
      <Button
        type="danger"
        icon={<StopOutlined />}
        size="small"
        onClick={showModal}
        className="bg-red-500 text-white hover:bg-red-600"
        style={{
          borderRadius: "6px",
          boxShadow: "0 2px 4px rgba(220, 38, 38, 0.2)",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        Annuler l'infraction
      </Button>

      <Modal
        title="Annulation de l'infraction"
        open={visible}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleOk}>
          <div className="mb-4">
            <p>Êtes-vous sûr de vouloir annuler cette infraction?</p>
            <span style={{ color: "rgba(0,0,0,0.45)" }}>Cette action est irréversible.</span>
          </div>

          <Form.Item
            name="cancellation_reason_id"
            label="Motif d'annulation"
            rules={[{ required: true, message: "Veuillez choisir un motif d'annulation" }]}
          >
            <Select placeholder="Sélectionnez un motif" loading={loadingMotifs}>
              {motifs.map((motif) => (
                <Select.Option key={motif.id} value={motif.id}>
                  {motif.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="cancellation_reason_description" label="Description (optionnel)">
            <TextArea rows={4} placeholder="Entrez une description supplémentaire" />
          </Form.Item>

          <div className="flex justify-end gap-2">
            <Button onClick={handleCancel}>Annuler</Button>
            <Button type="primary" danger htmlType="submit" loading={loadingMotifs} style={{ borderRadius: "6px" }}>
              Confirmer l'annulation
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}
